import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const VolunteerForm = () => {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    idNumber: '',
    age: '',
    educationLevel: '',
    educationDepartment: '',
    availability: '',
    skills: [],
    customSkill: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const savedLanguage = localStorage.getItem('memorial-language') || 'en';
    setCurrentLanguage(savedLanguage);

    // Listen for language changes
    const handleLanguageChange = (event) => {
      const newLanguage = event.detail?.language || 'en';
      setCurrentLanguage(newLanguage);
    };

    window.addEventListener('languageChanged', handleLanguageChange);
    return () => window.removeEventListener('languageChanged', handleLanguageChange);
  }, []);

  const formContent = {
    ar: {
      title: "تسجيل المتطوعين",
      description: "انضم إلى فريق المتطوعين وساهم في خدمة المجتمع",
      nameLabel: "الاسم الكامل",
      namePlaceholder: "أدخل اسمك الكامل",
      emailLabel: "البريد الإلكتروني",
      emailPlaceholder: "أدخل بريدك الإلكتروني",
      phoneLabel: "رقم الهاتف",
      phonePlaceholder: "أدخل رقم هاتفك",
      locationLabel: "الموقع",
      locationPlaceholder: "أدخل مدينتك أو منطقتك",
      availabilityLabel: "التوفر",
      availabilityPlaceholder: "اختر مدى توفرك",
      skillsLabel: "المهارات والاهتمامات",
      experienceLabel: "الخبرة السابقة",
      experiencePlaceholder: "اكتب عن خبرتك في العمل التطوعي...",
      motivationLabel: "الدافع للتطوع",
      motivationPlaceholder: "لماذا تريد أن تكون متطوعاً معنا؟",
      submitButton: "تسجيل كمتطوع",
      submittingButton: "جاري التسجيل...",
      successTitle: "مرحباً بك في الفريق!",
      successMessage: "تم تسجيلك بنجاح كمتطوع. سنتواصل معك قريباً بتفاصيل الفرص المتاحة.",
      volunteerCount: "متطوع",
      requiredError: "هذا الحقل مطلوب",
      emailError: "يرجى إدخال بريد إلكتروني صحيح",
      phoneError: "يرجى إدخال رقم هاتف صحيح",
      phoneLimitError: "تم استخدام هذا الرقم بالفعل. الحد الأقصى المسموح به هو إرسال واحد فقط.",
      idNumberLabel: "رقم الهوية",
      idNumberPlaceholder: "أدخل رقم هويتك",
      idNumberError: "يرجى إدخال رقم هوية صحيح",
      submitError: "حدث خطأ أثناء إرسال الطلب. يرجى المحاولة مرة أخرى.",
      ageLabel: "العمر",
      agePlaceholder: "أدخل عمرك",
      educationLevelLabel: "المستوى التعليمي",
      educationLevelPlaceholder: "اختر مستواك التعليمي",
      educationDepartmentLabel: "التخصص",
      educationDepartmentPlaceholder: "أدخل تخصصك",
      customSkillLabel: "مهارة أخرى",
      customSkillPlaceholder: "اكتب مهارة أخرى غير موجودة في القائمة",
      contactMessage: "سنتواصل معك عندما نحتاج إلى توظيفك في المكان المناسب"
    },
    en: {
      title: "Volunteer Registration",
      description: "Join our volunteer team and contribute to serving the community",
      nameLabel: "Full Name",
      namePlaceholder: "Enter your full name",
      emailLabel: "Email Address",
      emailPlaceholder: "Enter your email address",
      phoneLabel: "Phone Number",
      phonePlaceholder: "Enter your phone number",
      locationLabel: "Location",
      locationPlaceholder: "Enter your city or region",
      availabilityLabel: "Availability",
      availabilityPlaceholder: "Select your availability",
      skillsLabel: "Skills and Interests",
      experienceLabel: "Previous Experience",
      experiencePlaceholder: "Tell us about your volunteer experience...",
      motivationLabel: "Motivation to Volunteer",
      motivationPlaceholder: "Why do you want to volunteer with us?",
      submitButton: "Register as Volunteer",
      submittingButton: "Registering...",
      successTitle: "Welcome to the Team!",
      successMessage: "You have been successfully registered as a volunteer. We will contact you soon with available opportunities.",
      volunteerCount: "volunteers",
      requiredError: "This field is required",
      emailError: "Please enter a valid email address",
      phoneError: "Please enter a valid phone number",
      phoneLimitError: "This phone number has already been used. Maximum allowed is 1 submission only.",
      idNumberLabel: "ID Number",
      idNumberPlaceholder: "Enter your ID number",
      idNumberError: "Please enter a valid ID number",
      submitError: "Failed to submit. Please try again.",
      ageLabel: "Age",
      agePlaceholder: "Enter your age",
      educationLevelLabel: "Education Level",
      educationLevelPlaceholder: "Select your education level",
      educationDepartmentLabel: "Education Department",
      educationDepartmentPlaceholder: "Enter your education department",
      customSkillLabel: "Other Skill",
      customSkillPlaceholder: "Write a skill that is not listed above",
      contactMessage: "We will contact you when we need you"
    },
    tr: {
      title: "Gönüllü Kaydı",
      description: "Gönüllü ekibimize katılın ve topluma hizmet etmeye katkıda bulunun",
      nameLabel: "Ad Soyad",
      namePlaceholder: "Adınızı ve soyadınızı girin",
      emailLabel: "E-posta Adresi",
      emailPlaceholder: "E-posta adresinizi girin",
      phoneLabel: "Telefon Numarası",
      phonePlaceholder: "Telefon numaranızı girin",
      locationLabel: "Konum",
      locationPlaceholder: "Şehrinizi veya bölgenizi girin",
      availabilityLabel: "Müsaitlik",
      availabilityPlaceholder: "Müsaitliğinizi seçin",
      skillsLabel: "Beceriler ve İlgi Alanları",
      experienceLabel: "Önceki Deneyim",
      experiencePlaceholder: "Gönüllülük deneyiminizi anlatın...",
      motivationLabel: "Gönüllülük Motivasyonu",
      motivationPlaceholder: "Neden bizimle gönüllü olmak istiyorsunuz?",
      submitButton: "Gönüllü Olarak Kayıt Ol",
      submittingButton: "Kayıt Ediliyor...",
      successTitle: "Takıma Hoş Geldiniz!",
      successMessage: "Gönüllü olarak başarıyla kaydoldunuz. Mevcut fırsatlarla ilgili yakında sizinle iletişime geçeceğiz.",
      volunteerCount: "gönüllü",
      requiredError: "Bu alan gereklidir",
      emailError: "Lütfen geçerli bir e-posta adresi girin",
      phoneError: "Lütfen geçerli bir telefon numarası girin",
      phoneLimitError: "Bu telefon numarası zaten kullanıldı. Maksimum izin verilen sadece 1 gönderimdir.",
      idNumberLabel: "Kimlik Numarası",
      idNumberPlaceholder: "Kimlik numaranızı girin",
      idNumberError: "Lütfen geçerli bir kimlik numarası girin",
      submitError: "Gönderim başarısız oldu. Lütfen tekrar deneyin.",
      ageLabel: "Yaş",
      agePlaceholder: "Yaşınızı girin",
      educationLevelLabel: "Eğitim Seviyesi",
      educationLevelPlaceholder: "Eğitim seviyenizi seçin",
      educationDepartmentLabel: "Eğitim Bölümü",
      educationDepartmentPlaceholder: "Eğitim bölümünüzü girin",
      customSkillLabel: "Diğer Beceri",
      customSkillPlaceholder: "Yukarıdaki listede olmayan bir beceri yazın",
      contactMessage: "Size ihtiyacımız olduğunda sizinle iletişime geçeceğiz"
    }
  };

  const getAvailabilityOptions = () => {
    return [
      { 
        value: 'weekends', 
        label: currentLanguage === 'ar' ? 'عطلات نهاية الأسبوع' : 
               currentLanguage === 'tr' ? 'Hafta sonları' : 'Weekends only' 
      },
      { 
        value: 'evenings', 
        label: currentLanguage === 'ar' ? 'المساء في أيام الأسبوع' : 
               currentLanguage === 'tr' ? 'Hafta içi akşamları' : 'Weekday evenings' 
      },
      { 
        value: 'flexible', 
        label: currentLanguage === 'ar' ? 'مرن' : 
               currentLanguage === 'tr' ? 'Esnek' : 'Flexible schedule' 
      },
      { 
        value: 'fulltime', 
        label: currentLanguage === 'ar' ? 'دوام كامل' : 
               currentLanguage === 'tr' ? 'Tam zamanlı' : 'Full-time availability' 
      }
    ];
  };

  const getSkillsOptions = () => {
    return [
      { 
        value: 'translation', 
        label: currentLanguage === 'ar' ? 'الترجمة' : 
               currentLanguage === 'tr' ? 'Çeviri' : 'Translation' 
      },
      { 
        value: 'social_media', 
        label: currentLanguage === 'ar' ? 'وسائل التواصل الاجتماعي' : 
               currentLanguage === 'tr' ? 'Sosyal medya' : 'Social Media' 
      },
      { 
        value: 'event_planning', 
        label: currentLanguage === 'ar' ? 'تنظيم الفعاليات' : 
               currentLanguage === 'tr' ? 'Etkinlik planlama' : 'Event Planning' 
      },
      { 
        value: 'fundraising', 
        label: currentLanguage === 'ar' ? 'جمع التبرعات' : 
               currentLanguage === 'tr' ? 'Bağış toplama' : 'Fundraising' 
      },
      { 
        value: 'graphic_design', 
        label: currentLanguage === 'ar' ? 'التصميم الجرافيكي' : 
               currentLanguage === 'tr' ? 'Grafik tasarım' : 'Graphic Design' 
      },
      { 
        value: 'photography', 
        label: currentLanguage === 'ar' ? 'التصوير الفوتوغرافي' : 
               currentLanguage === 'tr' ? 'Fotoğrafçılık' : 'Photography' 
      },
      { 
        value: 'writing', 
        label: currentLanguage === 'ar' ? 'الكتابة' : 
               currentLanguage === 'tr' ? 'Yazarlık' : 'Writing' 
      },
      { 
        value: 'community_outreach', 
        label: currentLanguage === 'ar' ? 'التواصل المجتمعي' : 
               currentLanguage === 'tr' ? 'Toplum erişimi' : 'Community Outreach' 
      }
    ];
  };

  const educationLevelLabels = {
    ar: {
      high_school: 'ثانوية',
      diploma: 'دبلوم',
      bachelor: 'بكالوريوس',
      master: 'ماجستير',
      doctorate: 'دكتوراه',
      other: 'أخرى'
    },
    en: {
      high_school: 'High School',
      diploma: 'Diploma',
      bachelor: 'Bachelor',
      master: 'Master',
      doctorate: 'Doctorate',
      other: 'Other'
    },
    tr: {
      high_school: 'Lise',
      diploma: 'Diploma',
      bachelor: 'Lisans',
      master: 'Yüksek Lisans',
      doctorate: 'Doktora',
      other: 'Diğer'
    }
  };

  const educationDepartmentLabels = {
    ar: {
      engineering: 'الهندسة',
      medicine: 'الطب',
      law: 'القانون',
      business: 'إدارة الأعمال',
      education: 'التربية',
      arts: 'الآداب',
      science: 'العلوم',
      social_sciences: 'العلوم الاجتماعية',
      other: 'أخرى'
    },
    en: {
      engineering: 'Engineering',
      medicine: 'Medicine',
      law: 'Law',
      business: 'Business',
      education: 'Education',
      arts: 'Arts',
      science: 'Science',
      social_sciences: 'Social Sciences',
      other: 'Other'
    },
    tr: {
      engineering: 'Mühendislik',
      medicine: 'Tıp',
      law: 'Hukuk',
      business: 'İşletme',
      education: 'Eğitim',
      arts: 'Sanat',
      science: 'Fen',
      social_sciences: 'Sosyal Bilimler',
      other: 'Diğer'
    }
  };

  const getEducationLevelOptions = () => {
    const labels = educationLevelLabels[currentLanguage] || educationLevelLabels.en;
    return [
      { value: 'high_school', label: labels.high_school },
      { value: 'diploma', label: labels.diploma },
      { value: 'bachelor', label: labels.bachelor },
      { value: 'master', label: labels.master },
      { value: 'doctorate', label: labels.doctorate },
      { value: 'other', label: labels.other }
    ];
  };

  const getEducationDepartmentOptions = () => {
    const labels = educationDepartmentLabels[currentLanguage] || educationDepartmentLabels.en;
    return [
      { value: 'engineering', label: labels.engineering },
      { value: 'medicine', label: labels.medicine },
      { value: 'law', label: labels.law },
      { value: 'business', label: labels.business },
      { value: 'education', label: labels.education },
      { value: 'arts', label: labels.arts },
      { value: 'science', label: labels.science },
      { value: 'social_sciences', label: labels.social_sciences },
      { value: 'other', label: labels.other }
    ];
  };

  const content = formContent?.[currentLanguage];

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData?.name?.trim()) {
      newErrors.name = content?.requiredError;
    }
    
    if (!formData?.email?.trim()) {
      newErrors.email = content?.requiredError;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = content?.emailError;
    }
    
    if (!formData?.phone?.trim()) {
      newErrors.phone = content?.requiredError;
    } else if (!/^[\+]?[1-9][\d]{0,15}$/?.test(formData?.phone?.replace(/\s/g, ''))) {
      newErrors.phone = content?.phoneError;
    }

    if (!formData?.idNumber?.trim()) {
      newErrors.idNumber = content?.requiredError;
    }
    
    if (!formData?.availability) {
      newErrors.availability = content?.requiredError;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    setErrors({}); // Clear previous errors
    
    try {
      // Get API token from environment variable
      const apiToken = import.meta.env.VITE_API_SECRET_TOKEN || '';
      
      // Prepare submission data
      const submissionData = {
        formType: 'volunteer',
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        idNumber: formData.idNumber || '',
        age: formData.age || '',
        educationLevel: formData.educationLevel || '',
        educationDepartment: formData.educationDepartment || '',
        availability: formData.availability,
        skills: formData.skills, // Array will be handled by Google Apps Script
        customSkill: formData.customSkill || '',
        token: apiToken, // Include API token for authentication
        origin: window.location.origin // Include origin for CORS validation
      };

      // Get Google Script URL from environment variable
      const scriptUrl = import.meta.env.VITE_GOOGLE_SCRIPT_URL || '';
      
      if (!scriptUrl) {
        throw new Error('Google Script URL not configured');
      }
      
      // Send to Google Apps Script
      // CORS is required - no fallback to no-cors mode
      const response = await fetch(scriptUrl, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionData)
      });

      // Parse response
      const result = await response.json();
      
      // Check if submission was successful
      if (!result.success) {
        throw new Error(result.error || 'Failed to submit. Please try again.');
      }

      setIsSubmitting(false);
      setIsSubmitted(true);

      // Reset form after success
      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({
          name: '',
          email: '',
          phone: '',
          idNumber: '',
          age: '',
          educationLevel: '',
          educationDepartment: '',
          availability: '',
          skills: [],
          customSkill: ''
        });
      }, 3000);
      
    } catch (error) {
      console.error('Failed to submit:', error);
      setIsSubmitting(false);
      
      const errorMessage = error.message || 
                          content?.submitError || 
                          'Failed to submit. Please try again.';
      
      // Check if it's a phone limit error
      if (error.message && (error.message.includes('phone number') || error.message.includes('limit exceeded') || error.message.includes('already been used'))) {
        setErrors({ 
          phone: content?.phoneLimitError || errorMessage
        });
      } else {
        setErrors({ 
          submit: errorMessage
        });
      }
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSkillsChange = (skillValue, checked) => {
    setFormData(prev => ({
      ...prev,
      skills: checked 
        ? [...prev?.skills, skillValue]
        : prev?.skills?.filter(skill => skill !== skillValue)
    }));
  };

  if (isSubmitted) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center py-8"
      >
        <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="UserCheck" size={32} className="text-success" />
        </div>
        <h3 className="font-body text-xl font-semibold text-primary mb-2">
          {content?.successTitle}
        </h3>
        <p className="font-body text-text-secondary mb-6">
          {content?.successMessage}
        </p>
        <div className="bg-success/5 border border-success/20 rounded-lg p-4">
          <div className="flex items-center justify-center space-x-3">
            <Icon name="Users" size={20} className="text-success" />
            <span className="font-body text-sm text-success font-medium">
              342 {content?.volunteerCount}
            </span>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <div>
      <div className="text-center mb-8">
        <h3 className="font-body text-2xl font-semibold text-primary mb-2">
          {content?.title}
        </h3>
        <p className="font-body text-text-secondary">
          {content?.description}
        </p>
      </div>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label={content?.nameLabel}
            type="text"
            placeholder={content?.namePlaceholder}
            value={formData?.name}
            onChange={(e) => handleInputChange('name', e?.target?.value)}
            error={errors?.name}
            required
          />

          <Input
            label={content?.emailLabel}
            type="email"
            placeholder={content?.emailPlaceholder}
            value={formData?.email}
            onChange={(e) => handleInputChange('email', e?.target?.value)}
            error={errors?.email}
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label={content?.phoneLabel}
            type="number"
            placeholder={content?.phonePlaceholder}
            value={formData?.phone}
            onChange={(e) => handleInputChange('phone', e?.target?.value)}
            error={errors?.phone}
            required
          />

          <Input
            label={content?.idNumberLabel}
            type="text"
            placeholder={content?.idNumberPlaceholder}
            value={formData?.idNumber}
            onChange={(e) => handleInputChange('idNumber', e?.target?.value)}
            error={errors?.idNumber}
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label={content?.ageLabel}
            type="number"
            placeholder={content?.agePlaceholder}
            value={formData?.age}
            onChange={(e) => handleInputChange('age', e?.target?.value)}
            error={errors?.age}
            min="1"
            max="120"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Select
            label={content?.educationLevelLabel}
            placeholder={content?.educationLevelPlaceholder}
            options={getEducationLevelOptions()}
            value={formData?.educationLevel}
            onChange={(value) => handleInputChange('educationLevel', value)}
            error={errors?.educationLevel}
          />

          <Input
            label={content?.educationDepartmentLabel}
            type="text"
            placeholder={content?.educationDepartmentPlaceholder}
            value={formData?.educationDepartment}
            onChange={(e) => handleInputChange('educationDepartment', e?.target?.value)}
            error={errors?.educationDepartment}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Select
            label={content?.availabilityLabel}
            placeholder={content?.availabilityPlaceholder}
            options={getAvailabilityOptions()}
            value={formData?.availability}
            onChange={(value) => handleInputChange('availability', value)}
            error={errors?.availability}
            required
          />
        </div>

        <div>
          <label className="block font-body text-sm font-medium text-text-primary mb-3">
            {content?.skillsLabel}
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {getSkillsOptions()?.map((skill) => (
              <Checkbox
                key={skill?.value}
                label={skill?.label}
                checked={formData?.skills?.includes(skill?.value)}
                onChange={(e) => handleSkillsChange(skill?.value, e?.target?.checked)}
              />
            ))}
          </div>
          <div className="mt-4">
            <Input
              label={content?.customSkillLabel}
              type="text"
              placeholder={content?.customSkillPlaceholder}
              value={formData?.customSkill}
              onChange={(e) => handleInputChange('customSkill', e?.target?.value)}
              error={errors?.customSkill}
            />
          </div>
        </div>

        {errors?.submit && (
          <div className="bg-error/10 border border-error/20 rounded-lg p-4">
            <p className="font-body text-sm text-error text-center">
              {errors.submit}
            </p>
          </div>
        )}

        <div className="bg-muted/50 border border-border rounded-lg p-4 text-center">
          <p className="font-body text-sm text-text-secondary">
            {content?.contactMessage}
          </p>
        </div>

        <Button
          type="submit"
          variant="default"
          size="lg"
          loading={isSubmitting}
          disabled={isSubmitting}
          iconName="UserPlus"
          iconPosition="left"
          fullWidth
        >
          {isSubmitting ? content?.submittingButton : content?.submitButton}
        </Button>
      </form>
      {/* Volunteer Counter */}
      <div className="mt-8 text-center">
        <div className="inline-flex items-center space-x-3 bg-muted rounded-lg px-4 py-2">
          <Icon name="Users" size={16} className="text-primary" />
          <span className="font-body text-sm text-text-secondary">
            342 {content?.volunteerCount}
          </span>
        </div>
      </div>
    </div>
  );
};

export default VolunteerForm;